"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-12 rounded-lg bg-accent flex items-center justify-center mb-4">
          <Send className="size-6 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-medium text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-border hover:bg-accent transition-colors">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-xs font-semibold">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Evelyn" {...field} className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-xs font-semibold">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Sterling" {...field} className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-xs font-semibold">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="evelyn@signature.com" {...field} className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors px-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" rules={{
      required: "Subject is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-xs font-semibold">Event Type</FormLabel>
            <FormControl>
              <Input placeholder="Wedding, Gala, or Corporate Event" {...field} className="bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors px-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-xs font-semibold">Your Vision</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your dream occasion..." className="min-h-[150px] resize-y bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors px-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <Button type="submit" className="w-full sm:w-auto bg-gradient-to-r from-secondary to-white text-primary font-default font-medium border border-border shadow-sm hover:shadow-md transition-all" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Securing Date...
          </> : <>
            <Send className="mr-2 size-4" />
            Book a Consultation
          </>}
      </Button>
    </Form>;
}
export default function BookAConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const contactDetails = [{
    icon: <MapPin className="size-5 text-primary" />,
    title: "The Studio",
    details: ["456 Lavender Lane", "Ethereal Heights, NY 10012"]
  }, {
    icon: <Phone className="size-5 text-primary" />,
    title: "Direct Line",
    details: ["+1 (212) 555-0198", "+1 (212) 555-0199"]
  }, {
    icon: <Mail className="size-5 text-primary" />,
    title: "Inquiries",
    details: ["concierge@signatureoccasions.com", "events@signatureoccasions.com"]
  }];
  const businessHours = [{
    day: "Monday - Friday",
    hours: "10:00 AM - 7:00 PM"
  }, {
    day: "Saturday",
    hours: "11:00 AM - 5:00 PM"
  }, {
    day: "Sunday",
    hours: "By Appointment"
  }];
  return <section data-section-id="246452" id="book-a-consultation" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className="space-y-10">
            <div className="space-y-4">
              <span className="font-sans uppercase tracking-widest text-xs font-bold text-primary/60">Contact Us</span>
              <h2 className="text-4xl md:text-5xl font-default font-light tracking-tight text-foreground">
                Let's Design Your <br />Signature Moment
              </h2>
              <p className="text-lg text-muted-foreground max-w-md font-sans leading-relaxed">
                From intimate weddings to grand corporate galas, we bring your vision to life with effortless elegance and meticulous planning.
              </p>
            </div>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-1">
              {contactDetails.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-secondary/30 border border-border shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-default font-medium text-foreground mb-1 text-lg">
                      {item.title}
                    </h3>
                    {item.details.map((detail, j) => <p key={j} className="text-muted-foreground font-sans text-sm">
                        {detail}
                      </p>)}
                  </div>
                </div>)}
            </div>

            <Separator className="my-8 bg-border/50" />

            <div>
              <div className="flex items-center gap-3 mb-6">
                <Clock className="size-5 text-primary" />
                <h3 className="text-xl font-default font-medium text-foreground">
                  Studio Hours
                </h3>
              </div>
              <div className="space-y-3">
                {businessHours.map((schedule, i) => <div key={i} data-index={i} className="flex justify-between items-center max-w-sm border-b border-border/30 pb-2">
                    <span className="text-muted-foreground font-sans text-sm">{schedule.day}</span>
                    <span className="font-medium text-foreground font-sans text-sm">{schedule.hours}</span>
                  </div>)}
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-primary/5 rounded-3xl blur-3xl -z-10 hidden lg:block" />
            
            <Card className="border-border/50 shadow-xl shadow-primary/5 bg-card/80 backdrop-blur-sm rounded-lg">
              <CardHeader className="pb-6">
                <CardTitle className="text-3xl font-default font-light">Inquiry Form</CardTitle>
                <CardDescription className="font-sans italic">
                  Share your event details below and our lead planner will reach out within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="consultation_form_home" formId="consultation_form_home" sectionName="Contact Section" defaultValues={{
                firstName: "",
                lastName: "",
                email: "",
                subject: "",
                message: ""
              }} submitText="Book a Consultation" successMessage="Your vision has been shared with our team. We look forward to crafting something beautiful together.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}